import { baseFieldClasses } from '@odo/shared-ui/components/fieldClasses';
import {
    MAX_TRIGGER_ALIGNER,
    MAX_TRIGGER_NOGOS,
    MAX_TRIGGER_WEEKS,
    MIN_TRIGGER_ALIGNER,
    MIN_TRIGGER_NOGOS,
    MIN_TRIGGER_WEEKS,
    type PlannableTriggerKind,
    type VirtualAppointmentTriggerKind,
    type VirtualAppointmentWeeksFrom
} from '../../services/virtualAppointments';
import { SegmentedToggle, type SegmentedOption } from './SegmentedToggle';
import { FieldLabel } from './VirtualAppointmentFieldParts';
import { NOGOS_RULE_HINT } from './virtualAppointmentNogos';
import { OPENED_NOW_LABEL } from '@odo/shared-ui/clinical/virtualAppointments/summary';

export interface WeeksFromChoice {
    value: VirtualAppointmentWeeksFrom;
    /** Set without a Treatment-phase start: "From treatment start" is disabled and this says why. */
    treatmentStartUnavailableHint?: string;
    onChange: (value: VirtualAppointmentWeeksFrom) => void;
}

interface VirtualAppointmentTriggerFieldProps {
    inputId: string;
    triggerKind: VirtualAppointmentTriggerKind;
    triggerAligner: string;
    triggerWeeks: string;
    triggerNogos: string;
    error?: string;
    /** Offers counting week N from now; absent always counts from the treatment start. */
    weeksFrom?: WeeksFromChoice;
    nogosPlaceholder?: string;
    /** Set when a no-go trigger can't be used, as without a DM link; disables No-gos. */
    nogosUnavailableHint?: string;
    /** Where the picked trigger counts from, shown under the row. */
    baseline?: string | null;
    onKindChange: (kind: PlannableTriggerKind) => void;
    onAlignerChange: (value: string) => void;
    onWeeksChange: (value: string) => void;
    onNogosChange: (value: string) => void;
}

function triggerOptions(nogosDisabled: boolean): SegmentedOption<VirtualAppointmentTriggerKind>[] {
    return [
        { value: 'aligner', label: 'Aligner' },
        { value: 'weeks', label: 'Week' },
        { value: 'nogos', label: 'No-gos', disabled: nogosDisabled }
    ];
}

function weeksFromOptions(
    treatmentStartDisabled: boolean
): SegmentedOption<VirtualAppointmentWeeksFrom>[] {
    return [
        {
            value: 'treatment_start',
            label: 'From treatment start',
            disabled: treatmentStartDisabled
        },
        { value: 'now', label: 'From now' }
    ];
}

const WEEKS_RANGE = `(${MIN_TRIGGER_WEEKS}-${MAX_TRIGGER_WEEKS})`;

// Short enough for the narrow Snooze modal; the From toggle under it says what the count is from.
function weeksPlaceholder(weeksFrom: WeeksFromChoice | undefined): string {
    return weeksFrom?.value === 'now' ? `Weeks ${WEEKS_RANGE}` : `Week # ${WEEKS_RANGE}`;
}

// Its own row under the kind toggle, so the week input keeps its width.
function WeeksFromToggle({ choice }: { choice: WeeksFromChoice }) {
    return (
        <div className="mt-2 flex">
            <SegmentedToggle
                value={choice.value}
                options={weeksFromOptions(choice.treatmentStartUnavailableHint !== undefined)}
                onChange={choice.onChange}
                ariaLabel="Count weeks from"
            />
        </div>
    );
}

interface NumberInputProps {
    id: string;
    value: string;
    placeholder: string;
    invalid: boolean;
    onChange: (value: string) => void;
}

// Wraps under the kind toggle when the field is too narrow for both on one line.
function NumberInput({ id, value, placeholder, invalid, onChange }: NumberInputProps) {
    const errorClasses = invalid ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : '';
    return (
        <div className="flex-1 min-w-[96px]">
            <input
                id={id}
                type="text"
                inputMode="numeric"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className={`${baseFieldClasses} ${errorClasses} h-9`}
            />
        </div>
    );
}

// The no-go counting rule when picked, then why the disabled options can't be picked. One per line.
function triggerHint(
    triggerKind: VirtualAppointmentTriggerKind,
    weeksFrom: WeeksFromChoice | undefined,
    nogosUnavailableHint: string | undefined
) {
    const rule = triggerKind === 'nogos' ? NOGOS_RULE_HINT : undefined;
    const weeksHint =
        triggerKind === 'weeks' ? weeksFrom?.treatmentStartUnavailableHint : undefined;
    const text = [rule, weeksHint, nogosUnavailableHint].filter(Boolean).join('\n');
    return text || undefined;
}

export function VirtualAppointmentTriggerField({
    inputId,
    triggerKind,
    triggerAligner,
    triggerWeeks,
    triggerNogos,
    error,
    weeksFrom,
    nogosPlaceholder = `No-gos (${MIN_TRIGGER_NOGOS}-${MAX_TRIGGER_NOGOS})`,
    nogosUnavailableHint,
    baseline,
    onKindChange,
    onAlignerChange,
    onWeeksChange,
    onNogosChange
}: VirtualAppointmentTriggerFieldProps) {
    const invalid = Boolean(error);
    // An existing "opened now" VA shows no option selected until a plannable kind is picked.
    const handleKind = (kind: VirtualAppointmentTriggerKind) => {
        if (kind === 'now') return;
        onKindChange(kind);
        // Without a treatment start only "From now" can fire, so picking Week selects it.
        const startUnavailable = weeksFrom?.treatmentStartUnavailableHint !== undefined;
        if (kind === 'weeks' && startUnavailable && weeksFrom.value !== 'now') {
            weeksFrom.onChange('now');
        }
    };

    return (
        <div className="flex flex-col">
            <FieldLabel
                htmlFor={inputId}
                required
                error={error}
                hint={triggerHint(triggerKind, weeksFrom, nogosUnavailableHint)}
            >
                Trigger
            </FieldLabel>
            <div className="flex flex-wrap items-center gap-2">
                <div className="w-full max-w-[240px] flex-shrink-0">
                    <SegmentedToggle
                        value={triggerKind}
                        options={triggerOptions(nogosUnavailableHint !== undefined)}
                        onChange={handleKind}
                        ariaLabel="Trigger kind"
                    />
                </div>
                {triggerKind === 'aligner' && (
                    <NumberInput
                        id={inputId}
                        value={triggerAligner}
                        placeholder={`Aligner # (${MIN_TRIGGER_ALIGNER}-${MAX_TRIGGER_ALIGNER})`}
                        invalid={invalid}
                        onChange={onAlignerChange}
                    />
                )}
                {triggerKind === 'weeks' && (
                    <NumberInput
                        id={inputId}
                        value={triggerWeeks}
                        placeholder={weeksPlaceholder(weeksFrom)}
                        invalid={invalid}
                        onChange={onWeeksChange}
                    />
                )}
                {triggerKind === 'nogos' && (
                    <NumberInput
                        id={inputId}
                        value={triggerNogos}
                        placeholder={nogosPlaceholder}
                        invalid={invalid}
                        onChange={onNogosChange}
                    />
                )}
                {triggerKind === 'now' && (
                    <span id={inputId} className="text-sm text-gray-500">
                        {OPENED_NOW_LABEL}
                    </span>
                )}
            </div>
            {triggerKind === 'weeks' && weeksFrom && <WeeksFromToggle choice={weeksFrom} />}
            {baseline && <p className="mt-1 break-words text-xs text-gray-500">{baseline}</p>}
        </div>
    );
}
